import { useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import Navbar, { NAVBAR_HEIGHT } from './Navbar'
import Sidebar, { SIDEBAR_RAIL_WIDTH, SIDEBAR_WIDTH } from './Sidebar'

interface Props {
    children: ReactNode
    withSidebar?: boolean
}

const SIDEBAR_COLLAPSED_KEY = 'condogest_sidebar_collapsed'

function readCollapsed() {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true' } catch { return false }
}

export default function AppLayout({ children, withSidebar = true }: Props) {
    const { pathname } = useLocation()
    // O drawer móvel abre "para" o URL actual: ao navegar (por toque ou por código) fecha sozinho.
    const [openAt, setOpenAt] = useState<string | null>(null)
    const mobileOpen = openAt === pathname
    const setMobileOpen = (open: boolean) => setOpenAt(open ? pathname : null)
    // Persistido para que a preferência sobreviva a um refresh/nova navegação
    // de topo (AppLayout não remonta ao navegar dentro do mesmo condomínio).
    const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsed)

    const toggleSidebarCollapsed = () => {
        setSidebarCollapsed((prev) => {
            const next = !prev
            try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)) } catch { /* sem storage: só nesta sessão */ }
            return next
        })
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <Navbar
                showMenuButton={withSidebar}
                mobileOpen={mobileOpen}
                onMenuClick={() => setMobileOpen(!mobileOpen)}
            />

            {withSidebar && (
                <Sidebar
                    mobileOpen={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    collapsed={sidebarCollapsed}
                    onToggleCollapse={toggleSidebarCollapsed}
                />
            )}

            <Box
                component="main"
                id="conteudo"
                sx={{
                    ml: withSidebar ? { xs: 0, md: `${sidebarCollapsed ? SIDEBAR_RAIL_WIDTH : SIDEBAR_WIDTH}px` } : 0,
                    transition: (theme) => theme.transitions.create('margin', { duration: theme.transitions.duration.shorter }),
                    // Margem inferior mínima + safe-area do dispositivo (barra de
                    // gestos/endereço em iOS/Android e em PWA instalada), para que
                    // o último elemento interativo de cada página (ex: botão
                    // "Guardar" no fundo de um formulário) nunca fique colado à
                    // borda do ecrã ou escondido atrás da barra do browser.
                    pb: 'calc(24px + env(safe-area-inset-bottom, 0px))',
                }}
            >
                {/* Espaçador para compensar a Navbar fixa */}
                <Toolbar sx={{ minHeight: `${NAVBAR_HEIGHT}px !important` }} />
                {children}
            </Box>
        </Box>
    )
}
